import React, {useLayoutEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  Hub,
  PermissionCustomRole,
  PermissionObject
} from 'yougile-model'
import {Sync} from '@client/sync'
import {IconButtonNew} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {getIcon} from '@components/permissions/permissions-settings/permissions-settings-utils'
import {arePermissionsChangedForObject} from '@components/project-participants/helpers'
import {noPropagate, useTooltipVisible} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {Project} from '@extensions/project'
import TreeLastSvg from './tree-last.svg'
import TreeLeafLastSvg from './tree-leaf-last.svg'
import TreeLeafNotLastSvg from './tree-leaf-not-last.svg'
import TreeLineSvg from './tree-line.svg'
import TreeNotLastSvg from './tree-not-last.svg'

interface TreeItem<K extends string, T> {
  key: K
  data: T
  parent: TreeItem<K, T> | undefined
  children?: TreeItem<K, T>[]
}

interface ObjectData {
  type: PermissionObject['type']
  hub: Hub
}

type ObjectTreeNode = TreeItem<DataObjectId, ObjectData>
type NodeMap = Map<DataObjectId, ObjectTreeNode>

const prepareTree = (
  projectId: DataObjectId,
  expandedKeys: Set<DataObjectId> = new Set()
): NodeMap | undefined => {
  const box = Sync.getCurrentSandbox()
  const project = box.getHub(projectId)
  if (!project) {
    return undefined
  }

  const nodeMap: NodeMap = new Map()

  const projectNode: ObjectTreeNode = {
    key: project.id,
    data: {type: 'project', hub: project},
    parent: undefined,
    children: []
  }
  nodeMap.set(projectNode.key, projectNode)

  if (expandedKeys.has(project.id)) {
    const boards = Project.listBoards(project, true)
    for (const board of boards) {
      const boardNode: ObjectTreeNode = {
        key: board.id,
        data: {type: 'board', hub: board},
        parent: projectNode,
        children: []
      }
      nodeMap.set(boardNode.key, boardNode)
      projectNode.children?.push(boardNode)

      if (expandedKeys.has(board.id)) {
        const columns = Board.listColumns(board, {
          sorted: true,
          noMirrors: true
        })
        for (const column of columns) {
          const columnNode: ObjectTreeNode = {
            key: column.id,
            data: {type: 'column', hub: column},
            parent: boardNode,
            children: []
          }
          nodeMap.set(columnNode.key, columnNode)
          boardNode.children?.push(columnNode)

          if (expandedKeys.has(column.id)) {
            const tasks = Column.listTasks(column, false, {sorted: true})
            for (const task of tasks) {
              const taskNode: ObjectTreeNode = {
                key: task.id,
                data: {type: 'task', hub: task},
                parent: columnNode
              }
              nodeMap.set(taskNode.key, taskNode)
              columnNode.children?.push(taskNode)
            }
          }
        }
      }
    }
  }

  return nodeMap
}

const TwTreeNode = tw.div<{$selected: boolean}>`
  cursor-pointer
  pl-16
  pr-4
  flex
  gap-4
  items-center
  ${p => cn(p.$selected ? 'bg-background-active' : 'hover:bg-background-hover')}
`

const TwTitle = tw.div<{$selected: boolean}>`
  flex-1
  whitespace-nowrap
  text-ellipsis
  overflow-hidden
  select-none
  ${p => (p.$selected ? 'text-sm-semibold' : 'text-sm-regular')}
`

export const TwTooltipText = tw.span`
  break-words
  line-clamp-7
`

const TwStatus = tw.div<{$changed: boolean}>`
  p-8
  text-xs-regular
  whitespace-nowrap
  select-none
  ${p => (p.$changed ? 'text-success' : 'text-placeholder')}
`

const Path = ({path, isLeaf}: {path: boolean[]; isLeaf: boolean}) => (
  <>
    {path.map((isLast, i, xs) => {
      if (i === xs.length - 1) {
        if (isLeaf) {
          return isLast ? (
            <TreeLeafLastSvg key={i} className="text-line-base" />
          ) : (
            <TreeLeafNotLastSvg key={i} className="text-line-base" />
          )
        }
        return isLast ? (
          <TreeLastSvg key={i} className="text-line-base" />
        ) : (
          <TreeNotLastSvg key={i} className="text-line-base" />
        )
      } else {
        return isLast ? (
          <div key={i} className="w-16 h-32" />
        ) : (
          <TreeLineSvg key={i} className="text-line-base" />
        )
      }
    })}
  </>
)

const TreeNode = ({
  node,
  path,
  expanded = false,
  selected = false,
  changed = false,
  setSelected,
  onToggle
}: {
  node: ObjectTreeNode
  path: boolean[]
  expanded?: boolean
  selected?: boolean
  changed?: boolean
  setSelected: (id: DataObjectId, type: PermissionObject['type']) => void
  onToggle: (id: DataObjectId) => void
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const hub = node.data.hub
  const tooltipVisible = useTooltipVisible(ref, [hub.title])
  const chevronIcon = expanded ? 'IconChevronUp' : 'IconChevronRight'
  const itemIcon = getIcon(node.data.type, hub)
  return (
    <TwTreeNode
      $selected={selected}
      onClick={e => {
        noPropagate(e)
        setSelected(hub.id, node.data.type)
      }}
    >
      <Path path={path} isLeaf={node.data.type === 'task'} />
      {node.data.type !== 'task' && (
        <IconButtonNew
          icon={chevronIcon}
          onClick={e => {
            noPropagate(e)
            onToggle(hub.id)
          }}
          className="!h-24 !w-24 -mx-4"
        />
      )}
      <Icon icon={itemIcon} color="icon-secondary" />
      <Tooltip
        content={<TwTooltipText>{hub.title}</TwTooltipText>}
        placement="bottom"
        disabled={!tooltipVisible}
        offset={[0, 8]}
      >
        <TwTitle ref={ref} $selected={selected}>
          {hub.title}
        </TwTitle>
      </Tooltip>
      <TwStatus $changed={changed}>
        {changed
          ? Lang.msg.proj.statusEdited
          : Lang.msg.proj.statusDefault(node.data.type)}
      </TwStatus>
    </TwTreeNode>
  )
}

const TreeImpl = ({
  role,
  node,
  path = [],
  selectedId,
  expandedKeys,
  setSelected,
  onToggle
}: {
  role: PermissionCustomRole
  node: ObjectTreeNode
  path: boolean[]
  selectedId: DataObjectId | undefined
  expandedKeys: Set<DataObjectId>
  setSelected: (id: DataObjectId, type: PermissionObject['type']) => void
  onToggle: (id: DataObjectId) => void
}) => {
  const renderedNode = (
    <TreeNode
      node={node}
      path={path}
      setSelected={setSelected}
      onToggle={onToggle}
      selected={node.data.hub.id === selectedId}
      expanded={expandedKeys.has(node.key)}
      changed={arePermissionsChangedForObject(
        role,
        node.data.hub.id,
        node.data.type
      )}
    />
  )

  const renderedChildren = node.children?.map((childNode, i, xs) => (
    <TreeImpl
      key={childNode.key}
      role={role}
      node={childNode}
      path={[...path, i === xs.length - 1]}
      selectedId={selectedId}
      expandedKeys={expandedKeys}
      setSelected={setSelected}
      onToggle={onToggle}
    />
  ))

  return (
    <>
      {renderedNode}
      {renderedChildren}
    </>
  )
}

const TwRoleObjectsTree = tw.div`
  pt-8
  pb-16
`

interface RoleObjectsTreeProps {
  role: PermissionCustomRole
  projectId: DataObjectId
  isNewProject: boolean
  selectedId: DataObjectId | undefined
  setSelected: (id: DataObjectId, type: PermissionObject['type']) => void
  initialExpandedIds?: Set<DataObjectId>
}

export const RoleObjectsTree = (props: RoleObjectsTreeProps) => {
  const {role, projectId, selectedId, setSelected, initialExpandedIds} = props
  const [expandedKeys, setExpandedKeys] = useState(
    initialExpandedIds ?? new Set([projectId])
  )
  const [nodeMap, setNodeMap] = useState<NodeMap>()

  useLayoutEffect(() => {
    setNodeMap(prepareTree(projectId, expandedKeys))
  }, [projectId, expandedKeys])

  const onToggle = (key: DataObjectId) =>
    setExpandedKeys(prevExpandedKeys => {
      const expandedKeys = new Set(prevExpandedKeys)
      if (expandedKeys.has(key)) {
        expandedKeys.delete(key)

        // Collapse all descendants of a `key` node
        for (const expandedKey of expandedKeys) {
          let node = nodeMap?.get(expandedKey)
          if (node) {
            const keysToCollapse = [node.key]
            while (node) {
              if (node.parent?.key === key) {
                // Collapsed node is one of expanded node's ancestors
                for (const keyToCollapse of keysToCollapse) {
                  expandedKeys.delete(keyToCollapse)
                }
                break
              } else if (node.parent) {
                node = node.parent
                keysToCollapse.push(node.key)
              } else {
                break // Reached the root node
              }
            }
          }
        }
      } else {
        expandedKeys.add(key)
      }
      return expandedKeys
    })

  const root = nodeMap?.get(projectId)
  return root ? (
    <TwRoleObjectsTree>
      <TreeImpl
        role={role}
        node={root}
        path={[]}
        selectedId={selectedId}
        expandedKeys={expandedKeys}
        setSelected={setSelected}
        onToggle={onToggle}
      />
    </TwRoleObjectsTree>
  ) : null
}
